// domain/sla.ts — Timer SLA per-status (SOP §5 Matriks SLA Induk). Port slaInfo/slaBreached
// v0.18 (baris 479–494). MURNI (data + fmtDurMin); badge/teks HTML dirender di view/perlu.
// Backend: `at` log = ISO timestamptz → waktu-masuk-status = new Date(at).getTime()
// (parseIndoDateTime v0.18 tak perlu). Config (on/cfg) di-inject dari core/settings.

/** Menit per status (0 = tidak ada SLA). Dikerjakan pakai est_selesai bila diisi. */
export const DEFAULT_SLA_CFG: Record<string, number> = {
  'Diterima': 30, 'Diagnosa': 1440, 'Menunggu Persetujuan': 240, 'Dikerjakan': 480, 'Siap Diambil': 0,
};
export const DEFAULT_SLA_ON = true;

/** SLA proses FINANCE (Batch Pengaturan SLA). verifikasi (menit) = batas Finance memverifikasi
 *  setelah CS tandai lunas / ajukan refund; approval (menit) = batas O/M memutus approval §26;
 *  penagihanEskalasiHari = hari LEWAT jatuh-tempo sebelum eskalasi piutang. Semua settable di Pengaturan. */
export interface FinanceSlaCfg {
  verifikasiBayarMenit: number; verifikasiRefundMenit: number; approvalMenit: number; penagihanEskalasiHari: number;
}
export const DEFAULT_FINANCE_SLA: FinanceSlaCfg = {
  verifikasiBayarMenit: 1440, verifikasiRefundMenit: 1440, approvalMenit: 1440, penagihanEskalasiHari: 3,
};

/** Format durasi menit → "3 hr 2j" / "5j 10m" / "45m". Port fmtDurMin v0.18. */
export function fmtDurMin(m: number): string {
  m = Math.max(0, Math.round(m));
  if (m >= 1440) { const h = Math.floor(m / 1440), j = Math.floor((m % 1440) / 60); return h + ' hr' + (j ? ' ' + j + 'j' : ''); }
  if (m >= 60) return Math.floor(m / 60) + 'j' + (m % 60 ? ' ' + (m % 60) + 'm' : '');
  return m + 'm';
}

export interface SlaResult {
  applies: boolean;
  deadline?: number; enter?: number; min?: number;
  overMin?: number; leftMin?: number; breached?: boolean; ratio?: number | null;
  durMin?: number; done?: boolean;
}

const targetMin = (status: string, on: boolean, cfg: Record<string, number>): number =>
  (on ? (cfg[status] ?? 0) : 0);

/**
 * Info SLA untuk satu tiket. `enterMs` = waktu (ms) tiket MASUK status berjalan
 * (NaN bila tak diketahui). `estSelesai` = ISO estimasi selesai (khusus Dikerjakan).
 * Port slaInfo v0.18: Siap/Sudah Diambil tak ber-SLA; Dikerjakan pakai est_selesai+1hr.
 */
export function slaInfo(
  status: string, enterMs: number, estSelesai: string | null | undefined,
  on: boolean, cfg: Record<string, number>,
): SlaResult {
  if (status === 'Sudah Diambil' || status === 'Siap Diambil') return { applies: false };
  const min = targetMin(status, on, cfg);
  const enter = enterMs;
  let deadline = NaN;
  if (status === 'Dikerjakan' && estSelesai) {
    const d = new Date(estSelesai);
    if (!isNaN(d.getTime())) deadline = d.getTime() + 86400000;
  }
  if (isNaN(deadline)) {
    if (!min || isNaN(enter)) return { applies: false };
    deadline = enter + min * 60000;
  }
  const now = Date.now(), over = now - deadline;
  return {
    applies: true, deadline, enter, min,
    overMin: Math.round(over / 60000), leftMin: Math.round(-over / 60000),
    breached: over > 0,
    ratio: (!isNaN(enter) && min) ? ((now - enter) / (min * 60000)) : null,
  };
}

/**
 * Info SLA untuk PROSES FINANCE berbasis-durasi (verifikasi bayar/refund, approval).
 * `startMs` = waktu mulai (ms; mis. markLunas.at). `doneMs` = waktu selesai (verify.at) atau null bila belum.
 * `slaMin` = batas menit (0 = tak ber-SLA). Menghasilkan durasi + breached + sisa/lewat waktu.
 * MURNI (nowMs boleh di-inject utk test). Dipakai badge finance + KPI (kepatuhan/kecepatan) — fase berikut.
 */
export function financeSlaInfo(
  startMs: number, doneMs: number | null | undefined, slaMin: number, nowMs = Date.now(),
): SlaResult {
  if (isNaN(startMs) || !slaMin) return { applies: false };
  const done = doneMs != null && !isNaN(doneMs);
  const end = done ? (doneMs as number) : nowMs;
  const durMin = Math.round((end - startMs) / 60000);
  const deadline = startMs + slaMin * 60000;
  const over = end - deadline;
  return {
    applies: true, deadline, enter: startMs, min: slaMin, done,
    durMin, overMin: Math.round(over / 60000), leftMin: Math.round(-over / 60000),
    breached: over > 0, ratio: (end - startMs) / (slaMin * 60000),
  };
}

// ================= Kepatuhan SLA dari ticket_logs (feed KPI) =================
export interface LogEntry { status: string; at: string; catatan?: string | null }
export interface StatusPhase { status: string; durMin: number; closed: boolean }

/** Durasi (menit) tiap KEMUNCULAN status dari urutan log tiket. exit = log berikutnya (kronologis);
 *  status berjalan (tanpa log berikutnya) = now − masuk, `closed:false`. MURNI (nowMs injectable). */
export function statusDurations(logs: LogEntry[], nowMs = Date.now()): StatusPhase[] {
  const sorted = logs
    .map((l) => ({ status: l.status, ms: new Date(l.at).getTime() }))
    .filter((x) => !isNaN(x.ms))
    .sort((a, b) => a.ms - b.ms);
  const out: StatusPhase[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]!, next = sorted[i + 1];
    const end = next ? next.ms : nowMs;
    out.push({ status: cur.status, durMin: Math.max(0, Math.round((end - cur.ms) / 60000)), closed: !!next });
  }
  return out;
}

/** Kepatuhan SLA satu tiket: dari fase-fase yang SELESAI (closed) pada `statuses`, berapa yang ≤ SLA cfg.
 *  Fase berjalan diabaikan (belum tentu breach). Return {within,total} utk diakumulasi jadi %. */
export function slaCompliance(phases: StatusPhase[], statuses: string[], cfg: Record<string, number>): { within: number; total: number } {
  let within = 0, total = 0;
  for (const p of phases) {
    if (!p.closed || !statuses.includes(p.status)) continue;
    const lim = cfg[p.status] ?? 0;
    if (lim <= 0) continue; // status tanpa SLA tak dihitung
    total++;
    if (p.durMin <= lim) within++;
  }
  return { within, total };
}

/** Kepatuhan verifikasi FINANCE dari pasangan log (markLunas → verify) via catatan. within bila ≤ slaMin. */
export function financeVerifyCompliance(logs: LogEntry[], slaMin: number): { within: number; total: number } {
  if (slaMin <= 0) return { within: 0, total: 0 };
  const sorted = logs
    .map((l) => ({ ms: new Date(l.at).getTime(), c: l.catatan ?? '' }))
    .filter((x) => !isNaN(x.ms))
    .sort((a, b) => a.ms - b.ms);
  let startMs = NaN, within = 0, total = 0;
  for (const r of sorted) {
    if (r.c.includes('Ditandai LUNAS')) startMs = r.ms; // CS tandai lunas → mulai hitung
    else if (r.c.includes('TERVERIFIKASI') && !isNaN(startMs)) {
      total++;
      if ((r.ms - startMs) / 60000 <= slaMin) within++;
      startMs = NaN;
    }
  }
  return { within, total };
}

/** Waktu (ms) mulai fase finance yang MENUNGGU, dari log terbaru yang cocok. NaN bila tak ada.
 *  kind 'verify' = markLunas ("Ditandai LUNAS"); 'refund' = ajukan refund ("Refund…menunggu"). */
export function financePendingStartMs(logs: LogEntry[], kind: 'verify' | 'refund'): number {
  let ms = NaN;
  for (const l of logs) { // logs kronologis → ambil kemunculan TERBARU
    const c = l.catatan ?? '';
    const hit = kind === 'verify' ? c.includes('Ditandai LUNAS') : (c.includes('Refund') && c.includes('menunggu'));
    if (hit) { const t = new Date(l.at).getTime(); if (!isNaN(t)) ms = t; }
  }
  return ms;
}

/** Jumlah hari LEWAT jatuh tempo (0 bila belum/ tepat jatuh tempo). Basis tanggal lokal. */
export function overdueDays(jatuhTempoISO: string | null | undefined, todayISO: string): number {
  if (!jatuhTempoISO) return 0;
  const jt = new Date(jatuhTempoISO + 'T00:00:00').getTime(), td = new Date(todayISO + 'T00:00:00').getTime();
  if (isNaN(jt) || isNaN(td)) return 0;
  return Math.max(0, Math.floor((td - jt) / 86400000));
}

/** True bila tiket melewati SLA pada status berjalan. */
export function slaBreached(
  status: string, enterMs: number, estSelesai: string | null | undefined,
  on: boolean, cfg: Record<string, number>,
): boolean {
  const s = slaInfo(status, enterMs, estSelesai, on, cfg);
  return !!(s.applies && s.breached);
}
